import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { ArrowRight, Check } from "@phosphor-icons/react";

/**
 * Programmatic category landing pages — one root-level URL per high-intent
 * search term (e.g. /silent-auction-software, /gala-fundraising-platform).
 *
 * Adding a new category = one entry in CATEGORIES, no component changes.
 */

const CATEGORIES = {
    "silent-auction-software": {
        title: "Silent auction software for nonprofits",
        eyebrow: "Silent auction software",
        h1: <>The all-in-one <em className="text-accent">silent auction</em> platform.</>,
        lead: "Mobile bidding, proxy bids, anti-snipe extensions, big-screen scoreboards, and a printable bid-sheet fallback when Wi-Fi gives out. Built for the auctions that actually fund your mission.",
        hero: "https://images.unsplash.com/photo-1532954906987-87cb14d75e44?w=1600&h=900&fit=crop",
        hero_alt: "Silent auction items displayed on a banquet table at a charity gala",
        keyword: "silent auction software",
        bullets: [
            "Mobile bidding on any phone — no app download required",
            "Proxy / maximum bidding with automatic outbid push + SMS alerts",
            "Anti-snipe extensions so the last 30 seconds don't decide a $10,000 lot",
            "Printable bid sheets as a Wi-Fi fallback (so you're never paper-blind)",
            "Big-screen scoreboard with animated revenue thermometer",
            "Volunteer-stamped audit trail on every check-in and POS sale",
            "Buy-now pricing, reserve prices, lot quantities for table tiers",
            "AI-suggested starting bid / reserve / buy-now per item",
        ],
        body: [
            { h: "Why event nights stop being scary", p: "Most silent-auction problems happen in the last 30 minutes — Wi-Fi flakes, paper bid sheets get lost, volunteers can't find the spreadsheet that maps paddle 47 to Mrs. Henderson. CharityFundraiser is built so none of that ends your night. Bid sheets print on demand. The volunteer app keeps working offline. The audit trail tells you exactly who scanned what." },
            { h: "Set up in 4 minutes flat", p: "Upload items via CSV (template provided), or paste from your existing spreadsheet. The AI suggests a starting bid, reserve, and buy-now price for every item based on category and fair value. Brand your storefront with a logo and an accent color. Done." },
            { h: "Pricing that doesn't punish your gala", p: "Flat 2% on funds raised. No setup fee. No monthly subscription. No per-event license. Compare that to OneCause's $12K-$25K annual contracts or Handbid's $895-$1,995 per-event licenses — the math gets one-sided fast." },
        ],
        cta: "Run your next auction on CharityFundraiser",
    },
    "gala-fundraising-platform": {
        title: "Gala fundraising platform — auctions, tickets, paddle raise",
        eyebrow: "Gala fundraising platform",
        h1: <>The platform built around <em className="text-accent">gala night</em>.</>,
        lead: "Ticketing, sponsorships, silent + live auctions, paddle raise, post-event settlement, and the only AI Coordinator Playbook on the market. Run a $500K gala without duct-taping five vendors.",
        hero: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=1600&h=900&fit=crop",
        hero_alt: "Black-tie gala dinner with stage lighting and a paddle-raise auction underway",
        keyword: "gala fundraising platform",
        bullets: [
            "Tiered ticketing (General · Patron · VIP · Sponsor table) with promo codes",
            "Sponsorship packages with logo placement + benefits matrix",
            "Silent auction with mobile bidding + scoreboard",
            "Live auction tools with paddle-raise tier prompts ($1000/500/250/100/50)",
            "AI Coordinator Playbook: auto-generated Run of Show, smart checklists, auctioneer talking points",
            "QR-coded door check-in with volunteer audit trail",
            "Real-time scoreboard + big-screen Show Director remote",
            "One-link post-event settlement (donor pays auction + paddle + fees in one tap)",
        ],
        body: [
            { h: "Stop running your gala from a spreadsheet", p: "A typical $300K gala needs an auction tool, a ticketing tool, an email tool, a paddle-raise tracker, and a spreadsheet to reconcile everything afterward. CharityFundraiser collapses all five into one dashboard with a single donor record per attendee. The AI Coordinator Playbook even drafts your Run of Show and key-personnel handout so your event chair stops working 4 weekends in a row." },
            { h: "The post-event recovery you never had", p: "After every gala, CharityFundraiser auto-generates a plan-vs-actual KPI report (revenue, attendees, average gift, conversion). Multi-Event Trends shows year-over-year deltas across every gala you've ever run. Donor of the Year leaderboard makes top-donor stewardship a one-click Lob postcard send." },
            { h: "Volunteer audit trail (the killer feature)", p: "Every door scan is stamped with the volunteer ID. Every POS sale is stamped with the volunteer ID. After the gala you know exactly who scanned 80% of guests, who closed the biggest tabs, and who you should promote to next year's event chair. No other platform on the market gives you this." },
        ],
        cta: "Plan your next gala on CharityFundraiser",
    },
    "golf-tournament-fundraising-software": {
        title: "Golf tournament fundraising software for nonprofits",
        eyebrow: "Golf tournament software",
        h1: <>The fastest way to <em className="text-accent">sell out your tournament.</em></>,
        lead: "Foursome team management, hole sponsorships, mulligan packs, raffles, real-time scoring, and pin-flag silent-auction items. Built specifically for charity golf tournaments.",
        hero: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1600&h=900&fit=crop",
        hero_alt: "Golf course at sunrise with sponsorship signs along the fairway — golf tournament fundraising",
        keyword: "golf tournament fundraising software",
        bullets: [
            "Foursome team builder — captains can invite their own players",
            "Tiered sponsorships: title, presenting, hole, beverage cart, golf cart",
            "Mulligan packs, raffles, longest-drive / closest-to-the-pin contest sales",
            "Pin-flag silent auction (items donated from each hole sponsor)",
            "Online + on-course mobile bidding with proxy and anti-snipe",
            "QR-coded shotgun-start check-in with volunteer audit trail",
            "Real-time scoring leaderboard projected to a clubhouse screen",
            "AI Coordinator Playbook tailored to golf-tournament Run of Show",
        ],
        body: [
            { h: "Designed for the chair who's done it five years in a row", p: "Charity golf tournaments are repeat events with similar sponsors and players every year. CharityFundraiser remembers last year's foursomes, sponsorships, donor preferences, and silent-auction items so this year's setup takes minutes, not weekends." },
            { h: "Sponsorship sales that actually move", p: "Branded sponsorship packages with logo placement and a benefits matrix. Send a self-serve link to corporate partners — they pick their tier, upload their logo, and pay. Their logo auto-routes to pin flags, hole signs, the event page, and the printed handout." },
        ],
        cta: "Launch your tournament on CharityFundraiser",
    },
    "raffle-software-for-nonprofits": {
        title: "Raffle software for nonprofits — license-aware, electronic drawings",
        eyebrow: "Raffle software",
        h1: <>Sell more <em className="text-accent">raffle tickets,</em> stay license-compliant.</>,
        lead: "License-aware ticket sales, electronic drawings, prize-tier management, and built-in compliance reporting. Run 50/50 raffles, calendar raffles, sweepstakes — without the legal headache.",
        hero: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1600&h=900&fit=crop",
        hero_alt: "Raffle tickets and prize-display table at a charity event",
        keyword: "raffle software for nonprofits",
        bullets: [
            "License-aware ticket sales (configurable by state regulations)",
            "Electronic drawings with audit log for compliance",
            "Prize-tier management (1st place, 2nd place, etc.)",
            "Calendar raffles — daily-prize drawings across an entire month",
            "50/50 raffles with real-time jackpot thermometer on the big screen",
            "QR-coded purchase links for table-by-table volunteer selling",
            "Automatic winner notification via email + SMS",
            "Compliance report export ready for state submissions",
        ],
        body: [
            { h: "License-aware ticket sales", p: "State raffle laws vary wildly. CharityFundraiser supports per-state purchase rules so volunteers can't accidentally sell to a buyer in a non-licensed state. Built-in audit logs make annual state-compliance reporting a 5-minute job." },
            { h: "Bundled with the rest of your event", p: "Most raffle-only tools are standalone — you reconcile sales by hand. CharityFundraiser raffle sales auto-stamp the buyer's donor record, so next year's segmentation already knows who bought tickets last year." },
        ],
        cta: "Run your raffle on CharityFundraiser",
    },
    "peer-to-peer-fundraising-platform": {
        title: "Peer-to-peer fundraising platform with team leaderboards",
        eyebrow: "Peer-to-peer fundraising",
        h1: <>The <em className="text-accent">P2P platform</em> your fundraisers will actually use.</>,
        lead: "Branded personal pages, team leaderboards with live thermometers, social-share asset generation, and donate-to-vote campaigns. The peer-to-peer experience your supporters are willing to share.",
        hero: "https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=1600&h=900&fit=crop",
        hero_alt: "Volunteers in matching team t-shirts at a peer-to-peer fundraising walk",
        keyword: "peer-to-peer fundraising platform",
        bullets: [
            "Branded personal pages per fundraiser (photo, story, goal)",
            "Team leaderboards with live updating thermometers",
            "Social-share asset auto-generation (Open Graph card + suggested copy)",
            "Recurring giving tied to the P2P campaign",
            "Top-10 dashboards your fundraisers can screenshot and share",
            "Donate-to-vote campaigns where gifts increment a contestant's tally",
            "Captain views — team captains see only their team's data",
            "Built-in matching-gift prompts (Double the Donation integration)",
        ],
        body: [
            { h: "Why most P2P tools fail", p: "P2P only works when fundraisers actually want to share their page. Most platforms produce ugly personal pages that fundraisers are embarrassed to post on LinkedIn. CharityFundraiser pages match your brand, auto-generate gorgeous social-share cards, and surface fundraiser-friendly stats they want to brag about." },
            { h: "Donate-to-vote and themed contests", p: "Need to run a contestant-driven campaign (Cutest Dog, Mr/Miss Whatever, Class of '99 vs Class of '00)? Donate-to-vote is built in. Every dollar = N votes, configurable. Real-time vote tally on the big screen." },
        ],
        cta: "Launch your P2P campaign",
    },
    "text-to-give-platform": {
        title: "Text-to-Give platform — SMS shortcodes & shared inbox",
        eyebrow: "Text-to-Give platform",
        h1: <>Text-to-Give that <em className="text-accent">actually converts.</em></>,
        lead: "Dedicated short codes, keyword routing, replies into a shared admin inbox, and one-tap donation links. The 60-second giving experience donors finish.",
        hero: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&h=900&fit=crop",
        hero_alt: "Phone screen showing a Text-to-Give SMS giving prompt during a charity event",
        keyword: "text-to-give platform",
        bullets: [
            "Dedicated short codes with keyword routing (DOG → animal shelter, MUSIC → arts)",
            "Donor texts amount + keyword → instant secure-link reply",
            "Replies routed into a unified admin shared inbox",
            "First-time donors auto-enrolled in donor CRM with attribution",
            "Big-screen on-stage display: \"Text DOG to 53555 to give\"",
            "Real-time SMS giving ticker for the event display",
            "Tied to paddle raises (text increments the tier thermometer)",
            "Automatic thank-you SMS after the gift",
        ],
        body: [
            { h: "Why Text-to-Give on the stage matters", p: "Live appeals have a 30-second window where a donor decides to give. Text-to-Give shortens that window from \"pull out card, find phone, find URL, type address, type card\" to \"text two words.\" On-stage prompts paired with a big-screen ticker convert 4-7x better than QR-only appeals." },
            { h: "Shared inbox keeps you talking", p: "Every reply to your shortcode lands in a shared admin inbox (also used by your Talk-Us-Now widget and contact form). Reply via SMS or email from one screen, with status tracking. No more \"the texting line\" being someone's personal cell phone." },
        ],
        cta: "Add Text-to-Give to your next event",
    },
    "online-donation-platform": {
        title: "Online donation platform for nonprofits — recurring, tribute, employer matching",
        eyebrow: "Online donation platform",
        h1: <>Donation pages that <em className="text-accent">finish the gift.</em></>,
        lead: "Cover-the-fee toggle, tribute & in-memory-of, recurring giving, employer matching prompts, and Apple Pay / Google Pay / Venmo / PayPal alongside the card form. Built for the donor who's already typed half a dozen address fields today.",
        hero: "https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=1600&h=900&fit=crop",
        hero_alt: "Donor giving a credit card to a nonprofit volunteer at a fundraising event",
        keyword: "online donation platform",
        bullets: [
            "One-click suggested amounts ($25 / $50 / $100 / $250 / Other)",
            "Cover-the-fee toggle (donor absorbs Stripe + 2% so you net 100%)",
            "Recurring giving toggle (monthly / quarterly / annual)",
            "Tribute / in-honor-of / in-memory-of with family notification email",
            "Employer matching gift prompt (Double the Donation ready)",
            "Apple Pay, Google Pay, Venmo, PayPal alongside card form",
            "Custom domains and branded URLs",
            "Stripe Connect direct payouts to your bank",
        ],
        body: [
            { h: "Cover-the-fee, done right", p: "Every donation page includes a cover-the-fee toggle so donors absorb the 2.9% + $0.30 card processing and the 2% platform fee. With the toggle on, your nonprofit nets 100% of the gift. Default it on; donors who untoggle still give." },
            { h: "Recurring done right", p: "Recurring giving sits next to the amount selector — not three clicks deep. Donors who give monthly are worth 4-7x the annual value of one-time donors. Make it easy and they choose it." },
        ],
        cta: "Launch your donation page",
    },
};

export function getCategorySlugs() { return Object.keys(CATEGORIES); }

export default function CategoryLandingPage({ slug }) {
    const c = CATEGORIES[slug];
    if (!c) return <div className="p-20 text-center">Category page not found.</div>;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": c.title,
        "description": c.lead,
        "brand": { "@type": "Brand", "name": "CharityFundraiser" },
        "category": c.eyebrow,
        "offers": { "@type": "Offer", "priceCurrency": "USD", "price": "0", "description": "Flat 2% on funds raised. No setup, no monthly." },
        "image": c.hero,
    };

    return (
        <div className="bg-background min-h-screen" data-testid={`category-${slug}`}>
            <SEOHead
                title={`${c.title} | CharityFundraiser`}
                description={c.lead.slice(0, 160)}
                keywords={[c.keyword, "nonprofit fundraising platform", "charity software", `${c.keyword} 2026`]}
                canonical={`/${slug}`}
                image={c.hero}
                jsonLd={jsonLd}
            />
            <Navbar variant="marketing" />

            {/* HERO */}
            <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <p className="label-eyebrow text-accent">{c.eyebrow}</p>
                        <h1 className="font-heading text-4xl md:text-6xl mt-5 leading-[1.05]">{c.h1}</h1>
                        <p className="text-white/80 text-lg mt-6 max-w-xl leading-relaxed">{c.lead}</p>
                        <div className="flex flex-col sm:flex-row gap-3 mt-10">
                            <Link to="/signup-org" className="btn-accent" data-testid="cat-cta-primary">{c.cta} <ArrowRight size={14} weight="bold" /></Link>
                            <Link to="/demo" className="btn-outline border-white/40 text-white hover:bg-white/10" data-testid="cat-cta-demo">Try the 10-second sandbox</Link>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="aspect-[4/3] overflow-hidden border border-white/10">
                            <img src={c.hero} alt={c.hero_alt} className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURE LIST */}
            <section className="py-20 px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
                <p className="label-eyebrow text-accent">What you get</p>
                <h2 className="font-heading text-3xl md:text-5xl mt-4 max-w-3xl">{c.eyebrow} that fits how you actually work.</h2>
                <ul className="grid md:grid-cols-2 gap-5 mt-12">
                    {c.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3 ed-card p-5" data-testid={`cat-bullet-${i}`}>
                            <Check size={20} weight="bold" className="text-accent mt-0.5 shrink-0" />
                            <span className="text-base leading-relaxed">{b}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* BODY SECTIONS */}
            <section className="py-20 px-6 md:px-12 lg:px-24 max-w-4xl mx-auto space-y-12">
                {c.body.map((b, i) => (
                    <article key={i} className="border-l-4 border-accent pl-6" data-testid={`cat-body-${i}`}>
                        <h3 className="font-heading text-2xl md:text-3xl text-primary">{b.h}</h3>
                        <p className="text-base text-foreground/75 mt-3 leading-relaxed">{b.p}</p>
                    </article>
                ))}
            </section>

            {/* CROSS-LINKS */}
            <section className="py-12 px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
                <p className="label-eyebrow text-accent">Also explore</p>
                <div className="flex flex-wrap gap-2 mt-4">
                    {Object.keys(CATEGORIES).filter(s => s !== slug).map(s => (
                        <Link key={s} to={`/${s}`} className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-border hover:border-accent hover:text-accent transition-colors">
                            {CATEGORIES[s].eyebrow}
                        </Link>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-primary text-primary-foreground py-24 px-6 text-center">
                <div className="max-w-2xl mx-auto">
                    <p className="label-eyebrow text-accent">Ready when you are</p>
                    <h2 className="font-heading text-4xl md:text-5xl mt-4">Spin up your platform in 4 minutes.</h2>
                    <p className="mt-6 text-white/75">No setup fee, no monthly. We get paid only when you do.</p>
                    <Link to="/signup-org" className="btn-accent mt-10 inline-flex" data-testid="cat-cta-bottom">{c.cta} <ArrowRight size={14} weight="bold" /></Link>
                </div>
            </section>
            <Footer />
        </div>
    );
}
